/**
 * Zwergenwald - Versand-Countdown und Zustellspanne fuer blocks/zw-lieferinfo.liquid.
 *
 * Zwei Teile:
 *   1. Reine Datumslogik (exportierte Funktionen, ohne DOM). Laeuft auch unter Node und ist so
 *      gegen feste Daten testbar: Feiertage NRW, naechster Versandtag, Annahmeschluss, Spanne.
 *   2. Das Element <zw-versandzeit>, das die Texte ins HTML schreibt. Wird nur im Browser definiert.
 *
 * Grundsaetze:
 *   - Uhrzeit IMMER nach Europe/Berlin (Intl.DateTimeFormat mit timeZone), nie nach Geraetezeit.
 *     Wer in Lissabon um 11:30 bestellt, sieht dieselbe Aussage wie jemand in Bochum um 12:30.
 *   - Kalendertage werden als Mitternacht UTC gefuehrt. Damit verschiebt keine Sommerzeit
 *     einen Tag, und Wochentag und Datum sind eindeutig.
 *   - Aktualisierung hoechstens einmal pro Minute, ausgerichtet auf den Minutenwechsel.
 *     Kein Sekundenticker.
 *   - Ohne JavaScript bleibt das Element versteckt - dann steht nur die Lieferzeit-Zeile da.
 */

const TAG_MS = 86400000;
const WOCHENTAGE = ['Sonntag', 'Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag'];
const WOCHENTAGE_KURZ = ['So.', 'Mo.', 'Di.', 'Mi.', 'Do.', 'Fr.', 'Sa.'];
const SUCHGRENZE_TAGE = 400;

// ---------------------------------------------------------------------------
// 1. Reine Datumslogik
// ---------------------------------------------------------------------------

/** Kalendertag als Mitternacht UTC. monat ist 1-12. */
export function kalendertag(jahr, monat, tag) {
  return new Date(Date.UTC(jahr, monat - 1, tag));
}

export function tagPlus(datum, anzahl) {
  return new Date(datum.getTime() + anzahl * TAG_MS);
}

/** "2026-10-03" */
export function schluessel(datum) {
  return datum.toISOString().slice(0, 10);
}

/**
 * Ostersonntag nach dem anonymen gregorianischen Algorithmus (Meeus/Jones/Butcher),
 * gueltig fuer alle Jahre des gregorianischen Kalenders.
 */
export function osterSonntag(jahr) {
  const a = jahr % 19;
  const b = Math.floor(jahr / 100);
  const c = jahr % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const monat = Math.floor((h + l - 7 * m + 114) / 31);
  const tag = ((h + l - 7 * m + 114) % 31) + 1;
  return kalendertag(jahr, monat, tag);
}

const feiertagsSpeicher = new Map();

/** Gesetzliche Feiertage in Nordrhein-Westfalen als Menge von Schluesseln "JJJJ-MM-TT". */
export function feiertageNRW(jahr) {
  if (feiertagsSpeicher.has(jahr)) return feiertagsSpeicher.get(jahr);

  const ostern = osterSonntag(jahr);
  const tage = [
    kalendertag(jahr, 1, 1), // Neujahr
    tagPlus(ostern, -2), // Karfreitag
    tagPlus(ostern, 1), // Ostermontag
    kalendertag(jahr, 5, 1), // Tag der Arbeit
    tagPlus(ostern, 39), // Christi Himmelfahrt
    tagPlus(ostern, 50), // Pfingstmontag
    tagPlus(ostern, 60), // Fronleichnam
    kalendertag(jahr, 10, 3), // Tag der Deutschen Einheit
    kalendertag(jahr, 11, 1), // Allerheiligen
    kalendertag(jahr, 12, 25), // 1. Weihnachtstag
    kalendertag(jahr, 12, 26), // 2. Weihnachtstag
  ];
  const menge = new Set(tage.map(schluessel));
  feiertagsSpeicher.set(jahr, menge);
  return menge;
}

export function istFeiertagNRW(datum) {
  return feiertageNRW(datum.getUTCFullYear()).has(schluessel(datum));
}

const berlinFormat = new Intl.DateTimeFormat('de-DE', {
  timeZone: 'Europe/Berlin',
  year: 'numeric',
  month: 'numeric',
  day: 'numeric',
  hour: 'numeric',
  minute: 'numeric',
  hourCycle: 'h23',
});

/**
 * Berliner Ortszeit zu einem Zeitpunkt.
 * @returns {{ datum: Date, minuten: number }} Kalendertag (UTC-Mitternacht) und Minuten seit 0:00
 */
export function berlinZeit(zeitpunkt = new Date()) {
  const teile = berlinFormat.formatToParts(zeitpunkt);
  const wert = (typ) => Number(teile.find((t) => t.type === typ)?.value);
  return {
    datum: kalendertag(wert('year'), wert('month'), wert('day')),
    minuten: (wert('hour') % 24) * 60 + wert('minute'),
  };
}

/**
 * Zusaetzliche Schliesstage aus dem Textfeld: eine Zeile je Datum, TT.MM.JJJJ.
 * Kommas und Semikolons als Trenner werden auch akzeptiert. Unlesbares wird ignoriert.
 */
export function leseSchliesstage(text) {
  const menge = new Set();
  for (const eintrag of String(text ?? '').split(/[\s,;]+/)) {
    const treffer = /^(\d{1,2})\.(\d{1,2})\.(\d{4})$/.exec(eintrag.trim());
    if (!treffer) continue;
    const [tag, monat, jahr] = [Number(treffer[1]), Number(treffer[2]), Number(treffer[3])];
    const datum = kalendertag(jahr, monat, tag);
    if (datum.getUTCDate() === tag && datum.getUTCMonth() === monat - 1) menge.add(schluessel(datum));
  }
  return menge;
}

/**
 * @typedef {Object} Versandkonfig
 * @property {Set<number>} versandtage - Wochentage mit Versand, 0 = Sonntag ... 6 = Samstag
 * @property {Set<string>} schliesstage - zusaetzliche Schliesstage als "JJJJ-MM-TT"
 * @property {number} annahmeschluss - Minuten seit 0:00 Berliner Zeit (11:00 = 660)
 */

/** Versandtag = gewaehlter Wochentag, kein Feiertag in NRW, kein eigener Schliesstag. */
export function istVersandtag(datum, konfig) {
  return (
    konfig.versandtage.has(datum.getUTCDay()) &&
    !istFeiertagNRW(datum) &&
    !konfig.schliesstage.has(schluessel(datum))
  );
}

/**
 * Wann geht das Paket an GLS?
 * @param {{ datum: Date, minuten: number }} jetzt - Berliner Ortszeit
 * @param {Versandkonfig} konfig
 * @returns {{ datum: Date, heute: boolean, restMinuten: number | null } | null}
 */
export function naechsterVersand(jetzt, konfig) {
  if (istVersandtag(jetzt.datum, konfig) && jetzt.minuten < konfig.annahmeschluss) {
    return { datum: jetzt.datum, heute: true, restMinuten: konfig.annahmeschluss - jetzt.minuten };
  }
  for (let i = 1; i <= SUCHGRENZE_TAGE; i++) {
    const datum = tagPlus(jetzt.datum, i);
    if (istVersandtag(datum, konfig)) return { datum, heute: false, restMinuten: null };
  }
  return null;
}

/** Zustelltag bei GLS: Montag bis Freitag, kein Feiertag (NRW als Naeherung). */
export function istZustelltag(datum) {
  const wochentag = datum.getUTCDay();
  return wochentag >= 1 && wochentag <= 5 && !istFeiertagNRW(datum);
}

/**
 * Voraussichtliche Zustellung: min bzw. max Arbeitstage nach dem Versandtag.
 * @returns {{ von: Date, bis: Date } | null}
 */
export function zustellspanne(versanddatum, minTage, maxTage) {
  const von = Math.max(1, Math.min(minTage, maxTage));
  const bis = Math.max(1, minTage, maxTage);
  let datum = versanddatum;
  let gezaehlt = 0;
  let vonDatum = null;
  for (let i = 0; i < SUCHGRENZE_TAGE && gezaehlt < bis; i++) {
    datum = tagPlus(datum, 1);
    if (!istZustelltag(datum)) continue;
    gezaehlt++;
    if (gezaehlt === von) vonDatum = datum;
  }
  return vonDatum && gezaehlt === bis ? { von: vonDatum, bis: datum } : null;
}

const zweistellig = (zahl) => String(zahl).padStart(2, '0');

/** 720 -> "12", 750 -> "12:30" */
export function formatUhrzeit(minuten) {
  const stunde = Math.floor(minuten / 60);
  const minute = minuten % 60;
  return minute === 0 ? String(stunde) : `${stunde}:${zweistellig(minute)}`;
}

/** "Montag, 22.09." */
export function formatTag(datum) {
  return `${WOCHENTAGE[datum.getUTCDay()]}, ${zweistellig(datum.getUTCDate())}.${zweistellig(datum.getUTCMonth() + 1)}.`;
}

/** "Mo. 22.09." */
export function formatTagKurz(datum) {
  return `${WOCHENTAGE_KURZ[datum.getUTCDay()]} ${zweistellig(datum.getUTCDate())}.${zweistellig(datum.getUTCMonth() + 1)}.`;
}

/** 135 -> "2 Std. 15 Min.", 40 -> "40 Min." */
export function formatRest(minuten) {
  const stunden = Math.floor(minuten / 60);
  const rest = minuten % 60;
  if (!stunden) return `${rest} Min.`;
  return rest ? `${stunden} Std. ${rest} Min.` : `${stunden} Std.`;
}

/**
 * Setzt alle Texte zusammen - die einzige Stelle, an der Logik und Wortlaut zusammenkommen.
 *
 * @param {Date} zeitpunkt
 * @param {Versandkonfig} konfig
 * @param {{ heute: string, spaeter: string, spanne: string }} texte - Vorlagen aus den Block-Einstellungen
 * @param {{ countdown: boolean, spanne: boolean, laufzeitMin: number, laufzeitMax: number }} optionen
 * @returns {{ versand: string, spanne: string, ziffern: { stunden: string, minuten: string, text: string } | null }}
 *   leere Zeichenkette = Zeile nicht zeigen; ziffern nur, solange heute noch versendet wird
 */
export function versandTexte(zeitpunkt, konfig, texte, optionen) {
  const ergebnis = { versand: '', spanne: '', ziffern: null };
  const versand = naechsterVersand(berlinZeit(zeitpunkt), konfig);
  if (!versand) return ergebnis;

  if (optionen.countdown) {
    const vorlage = versand.heute ? texte.heute : texte.spaeter;
    ergebnis.versand = String(vorlage ?? '')
      .replaceAll('[uhrzeit]', formatUhrzeit(konfig.annahmeschluss))
      .replaceAll('[rest]', versand.restMinuten != null ? formatRest(versand.restMinuten) : '')
      .replaceAll('[tag]', formatTag(versand.datum))
      .trim();
    if (versand.heute && versand.restMinuten != null && ergebnis.versand) {
      ergebnis.ziffern = {
        stunden: zweistellig(Math.floor(versand.restMinuten / 60)),
        minuten: zweistellig(versand.restMinuten % 60),
        text: formatRest(versand.restMinuten),
      };
    }
  }

  if (optionen.spanne) {
    const spanne = zustellspanne(versand.datum, optionen.laufzeitMin, optionen.laufzeitMax);
    if (spanne) {
      let text = String(texte.spanne ?? '');
      if (schluessel(spanne.von) === schluessel(spanne.bis)) {
        text = text.replace(/\[von\]\s*[–-]\s*\[bis\]/, '[von]');
      }
      ergebnis.spanne = text
        .replaceAll('[von]', formatTagKurz(spanne.von))
        .replaceAll('[bis]', formatTagKurz(spanne.bis))
        .trim();
    }
  }

  return ergebnis;
}

// ---------------------------------------------------------------------------
// 2. Element <zw-versandzeit> - nur im Browser
// ---------------------------------------------------------------------------

/** Name des Ereignisses aus assets/zw-variantenblock.js - bewusst hier wiederholt, damit diese
 *  Datei ohne die Horizon-Module (und damit unter Node) ladbar bleibt. */
const ZW_AKTUALISIERT = 'zw:variantenblock-aktualisiert';

function konfigAus(daten) {
  const versandtage = new Set(
    String(daten.versandtage ?? '')
      .split(',')
      .map((wert) => Number.parseInt(wert, 10))
      .filter((wert) => wert >= 0 && wert <= 6)
  );
  return {
    versandtage,
    schliesstage: leseSchliesstage(daten.schliesstage),
    annahmeschluss: Number.parseInt(daten.annahmeschluss ?? '660', 10) || 0,
  };
}

function definiereElement() {
  class ZwVersandzeit extends HTMLElement {
    /** @type {ReturnType<typeof setTimeout> | undefined} */
    #zeitgeber;
    /** @type {Element | null} */
    #block = null;

    connectedCallback() {
      this.#block = this.closest('zw-variantenblock');
      this.#block?.addEventListener(ZW_AKTUALISIERT, this.#neuRechnen);
      document.addEventListener('visibilitychange', this.#neuRechnen);
      this.#neuRechnen();
    }

    disconnectedCallback() {
      clearTimeout(this.#zeitgeber);
      this.#block?.removeEventListener(ZW_AKTUALISIERT, this.#neuRechnen);
      document.removeEventListener('visibilitychange', this.#neuRechnen);
      this.#block = null;
    }

    #neuRechnen = () => {
      clearTimeout(this.#zeitgeber);
      this.#zeichnen();
      if (document.visibilityState === 'hidden' || this.dataset.verfuegbar !== 'true') return;
      // Auf den naechsten Minutenwechsel ausrichten; Berlin weicht von UTC nur um ganze Stunden ab.
      const bisMinutenwechsel = 60000 - (Date.now() % 60000) + 250;
      this.#zeitgeber = setTimeout(this.#neuRechnen, bisMinutenwechsel);
    };

    #zeichnen() {
      const versandZeile = this.querySelector('[data-zw-versand]');
      const spanneZeile = this.querySelector('[data-zw-spanne]');
      const daten = this.dataset;

      let texte = { versand: '', spanne: '' };
      if (daten.verfuegbar === 'true') {
        texte = versandTexte(
          new Date(),
          konfigAus(daten),
          { heute: daten.textHeute ?? '', spaeter: daten.textSpaeter ?? '', spanne: daten.textSpanne ?? '' },
          {
            countdown: daten.countdown === 'true',
            spanne: daten.spanne === 'true',
            laufzeitMin: Number.parseInt(daten.laufzeitMin ?? '1', 10) || 1,
            laufzeitMax: Number.parseInt(daten.laufzeitMax ?? '2', 10) || 1,
          }
        );
      }

      if (versandZeile) {
        versandZeile.textContent = texte.versand;
        versandZeile.hidden = !texte.versand;
      }
      // Ziffernfelder "02 14" neben dem Satz (Gestaltungsvorgabe Nr. 6) - nur fuer Augen, der
      // Screenreader liest die Restzeit aus dem aria-label.
      const ziffern = this.querySelector('[data-zw-ziffern]');
      if (ziffern) {
        const zeigen = daten.ziffern === 'true' && texte.ziffern != null;
        if (zeigen) {
          const [stunden, minuten] = ziffern.querySelectorAll('[data-zw-ziffer]');
          if (stunden) stunden.textContent = texte.ziffern.stunden;
          if (minuten) minuten.textContent = texte.ziffern.minuten;
          ziffern.setAttribute('aria-label', `${daten.textZiffern ?? 'noch'} ${texte.ziffern.text}`.trim());
        }
        ziffern.hidden = !zeigen;
      }
      if (spanneZeile) {
        spanneZeile.textContent = texte.spanne;
        spanneZeile.hidden = !texte.spanne;
      }
      this.hidden = !texte.versand && !texte.spanne;
    }
  }

  if (!customElements.get('zw-versandzeit')) {
    customElements.define('zw-versandzeit', ZwVersandzeit);
  }
}

if (typeof window !== 'undefined' && 'customElements' in window) {
  definiereElement();
}
